import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import {
  updateKbInvoice,
  type Invoice,
  type InvoiceFilters,
  type InvoiceTotal,
} from '../lib/invoicesApi';
import { useInvoiceSyncWatcher, useInvoices, useUpdateInvoiceProject } from '../hooks/useInvoices';
import { useEfacturaMessage, useSetEfacturaSettled } from '../hooks/useEfactura';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, Hash, Plus } from 'lucide-react';
import { useCompanies } from '../hooks/useCompanies';
import { useCreateIssuedInvoiceStorno, useIssuedInvoices } from '../hooks/useIssuedInvoices';
import { fetchIssuedInvoiceFile } from '../lib/issuedInvoicesApi';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { IssuedInvoiceStatusBadge } from '../components/domain/issuedInvoices/IssuedInvoiceStatusBadge';
import { InvoiceSeriesModal } from '../components/domain/issuedInvoices/InvoiceSeriesModal';
import { useProjects } from '../hooks/useProjects';
import { useActiveCompanyId } from '../store/useCompanyStore';
import { usePermissions } from '../hooks/usePermissions';
import { Select } from '../components/ui/Select';
import { DateField } from '../components/ui/DateField';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { EditWithAIButton } from '../components/ui/EditWithAIButton';
import { InvoiceDetailModal } from '../components/domain/invoices/InvoiceDetailModal';
import { InvoiceImportDialog } from '../components/domain/invoices/InvoiceImportDialog';
import {
  DetailFrame,
  EfacturaFileButtons,
  FIELD_CLASS,
  InvoiceDetails,
  KindBadge,
  LinesTable,
  PartyCard,
  SettledSwitch,
  date,
  dateTime,
  money,
  useEfacturaFiles,
} from '../components/domain/efactura/EfacturaShared';
import { useI18n } from '../contexts/I18nContext';

// FACTURI. Every invoice the firm holds — mirrored from ANAF SPV (permanent,
// SPV itself keeps them ~90 days), issued from Firmio, uploaded by hand or
// picked from email — in the same table, filters and expanded detail as the
// SPV e-Factura page, so one invoice looks the same wherever it is listed.
//
// The former "Facturi emise" page was merged in here (2026-09-29): issued
// invoices get an "emisă din Firmio" badge + ANAF's verdict on their row, the
// drafts waiting for approval sit in a strip above the table, and "Factură
// nouă" opens the issuing editor (/financials/issued/new, kept as is).

const DEFAULT_FILTERS: InvoiceFilters = { sort: 'issueDate', order: 'desc', page: 1, limit: 50 };

const TABS: Array<{ key: string; label: string; type: Invoice['direction'] | undefined }> = [
  { key: 'ALL', label: 'Toate', type: undefined },
  { key: 'OUTCOME', label: 'Primite', type: 'OUTCOME' },
  { key: 'INCOME', label: 'Emise', type: 'INCOME' },
];

const SOURCE_LABELS: Record<string, string> = {
  efactura: 'din SPV',
  firmio: 'emisă din Firmio',
  manual: 'adăugată manual',
  email: 'din email',
};

/** Column count of the table, for the expanded detail row. */
const COLUMNS = 9;

function kindOf(inv: Invoice): 'TRIMISA' | 'PRIMITA' {
  return inv.direction === 'INCOME' ? 'TRIMISA' : 'PRIMITA';
}

function settledLabel(inv: Invoice): string {
  return inv.direction === 'INCOME' ? 'Încasată' : 'Achitată';
}

/** "emisă din Firmio" pill; brand purple so it reads on both themes. */
function FirmioBadge() {
  return (
    <span
      className="ml-1 inline-block rounded border border-purple-300 bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800"
      title="Factură emisă din Firmio și trimisă în ANAF SPV"
    >
      emisă din Firmio
    </span>
  );
}

function issuedMoney(v: number, currency: string): string {
  return money(v, currency);
}

/**
 * Drafts issued from Firmio are not invoices yet (the number is allocated at
 * approval), so they do not appear in the table below; this strip lists them
 * so approval is one click away from Facturi.
 */
function IssuedDraftsStrip() {
  const drafts = useIssuedInvoices({ status: 'DRAFT', limit: 20 });
  const pending = useIssuedInvoices({ status: 'SENT', limit: 1 });
  const rows = drafts.data?.data ?? [];
  const pendingCount = pending.data?.meta.total ?? 0;
  if (rows.length === 0 && pendingCount === 0) return null;
  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
      {rows.length > 0 && (
        <>
          <div className="font-medium text-amber-900">
            {rows.length === 1
              ? 'O ciornă de factură așteaptă aprobarea'
              : `${rows.length} ciorne de factură așteaptă aprobarea`}
          </div>
          <ul className="mt-2 divide-y divide-amber-200/70">
            {rows.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5">
                <Link
                  to={`/financials/issued/${r.id}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {r.customer.name}
                </Link>
                <span className="text-gray-600">CUI {r.customer.cui}</span>
                <span className="text-gray-600">{date(r.issueDate)}</span>
                <span className="ml-auto font-medium text-gray-900">
                  {issuedMoney(r.totalAmount, r.currency)}
                </span>
                <Link
                  to={`/financials/issued/${r.id}`}
                  className="rounded border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Deschide și aprobă
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
      {pendingCount > 0 && (
        <p className={`text-xs text-amber-800 ${rows.length > 0 ? 'mt-2' : ''}`}>
          {pendingCount === 1
            ? 'O factură trimisă în SPV așteaptă răspunsul ANAF; se verifică automat la câteva minute.'
            : `${pendingCount} facturi trimise în SPV așteaptă răspunsul ANAF; se verifică automat la câteva minute.`}
        </p>
      )}
    </div>
  );
}

function TotalsRow({ totals }: { totals: InvoiceTotal[] }) {
  const cells = [...totals].sort(
    (a, b) => a.direction.localeCompare(b.direction) || a.currency.localeCompare(b.currency),
  );
  if (cells.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3 text-sm">
      {cells.map((t) => (
        <div
          key={`${t.direction}-${t.currency}`}
          className="rounded-md border border-gray-200 bg-white px-3 py-2"
        >
          <div className="text-xs uppercase tracking-wide text-gray-500">
            {t.direction === 'INCOME' ? 'Emise' : 'Primite'} · {t.currency}
          </div>
          <div className="font-semibold text-gray-900">{money(t.totalAmount, t.currency)}</div>
          <div className="text-xs text-gray-500">{t.count} documente</div>
        </div>
      ))}
    </div>
  );
}

/** Document view for rows that did not come through SPV (Firmio, manual, email). */
function LocalInvoiceDetails({ inv, ownName }: { inv: Invoice; ownName: string }) {
  const isIncome = inv.direction === 'INCOME';
  const partner = isIncome ? inv.client : inv.vendor;
  const net = inv.amount - inv.taxAmount;
  return (
    <div className="space-y-3 text-sm">
      <div className="grid gap-3 sm:grid-cols-2">
        <PartyCard label="Furnizor" name={isIncome ? ownName : partner} />
        <PartyCard label="Cumpărător" name={isIncome ? partner : ownName} />
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-700">
        <span>
          Tip: <strong>{inv.amount < 0 ? 'Notă de credit (storno)' : 'Factură'}</strong>
        </span>
        <span>
          Scadență: <strong>{date(inv.dueDate)}</strong>
        </span>
        {inv.project && (
          <span>
            Proiect: <strong>{inv.project.name}</strong>
          </span>
        )}
        {inv.service && (
          <span>
            Serviciu: <strong>{inv.service.name}</strong>
          </span>
        )}
        {inv.paidAt && (
          <span>
            {settledLabel(inv)} la: <strong>{date(inv.paidAt)}</strong>
          </span>
        )}
      </div>
      {inv.items.length > 0 && (
        <LinesTable
          currency={inv.currency}
          lines={inv.items.map((it, i) => ({
            id: String(i + 1),
            description: it.description,
            quantity: it.quantity,
            unit: 'buc',
            unitPrice: it.unitPrice,
            vat: '',
            lineTotal: it.amount,
          }))}
        />
      )}
      <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 text-gray-700">
        <span>
          Fără TVA: <strong>{money(net, inv.currency)}</strong>
        </span>
        <span>
          TVA: <strong>{money(inv.taxAmount, inv.currency)}</strong>
        </span>
        <span>
          Cu TVA: <strong>{money(inv.amount, inv.currency)}</strong>
        </span>
      </div>
      {inv.notes && <p className="text-gray-600">{inv.notes}</p>}
    </div>
  );
}

/**
 * "Proiect" picker inside the expanded row: attach the invoice to a project
 * without opening the edit form. The project's Resurse → Facturi list is
 * exactly the invoices carrying its id, so this is how an invoice from here
 * lands there. Works for every source, SPV mirrors included.
 */
function ProjectPicker({ inv }: { inv: Invoice }) {
  const { data: projectsData } = useProjects({ sort: 'name', limit: 100 });
  const mutation = useUpdateInvoiceProject(inv.id);
  const options = useMemo(
    () =>
      (projectsData?.projects ?? []).map((p: { id: string; name: string }) => ({
        value: p.id,
        label: p.name,
      })),
    [projectsData],
  );
  return (
    <div className="flex flex-wrap items-end gap-2 text-sm text-gray-700">
      <Select
        label="Proiect"
        value={inv.project?.id ?? ''}
        onChange={(v) => mutation.mutate(v || null)}
        options={options}
        placeholder="Fără proiect"
        clearable
        searchable
        width={280}
        className="w-64"
      />
      {mutation.isPending && <span className="text-xs text-gray-500">Se salvează…</span>}
      {mutation.isError && (
        <span className="text-xs text-red-600">
          {mutation.error instanceof Error ? mutation.error.message : 'Nu s-a putut salva.'}
        </span>
      )}
      {inv.project && (
        <Link
          to={`/projects/${encodeURIComponent(inv.project.id)}?tab=resources`}
          className="text-xs text-brand-700 underline decoration-brand-700/40 hover:decoration-brand-700"
        >
          Vezi în Resurse
        </Link>
      )}
    </div>
  );
}

function InvoiceDetailRow({
  inv,
  ownName,
  onEdit,
  onClose,
}: {
  inv: Invoice;
  ownName: string;
  onEdit: () => void;
  onClose: () => void;
}) {
  const spvId = inv.efacturaMessageId;
  const detail = useEfacturaMessage(spvId);
  const files = useEfacturaFiles(spvId, detail.data?.anafId ?? null);
  const spv = detail.data;
  const issued = inv.issuedInvoice;
  const [issuedPdfBusy, setIssuedPdfBusy] = useState(false);
  const [issuedPdfError, setIssuedPdfError] = useState<string | null>(null);

  // Storno: the reversal of an invoice ANAF already holds. The button only
  // writes the draft (and asks ANAF to validate it); issuing it is a separate,
  // deliberate act in the tab that opens.
  const confirm = useConfirm();
  const storno = useCreateIssuedInvoiceStorno();
  const [stornoError, setStornoError] = useState<string | null>(null);
  const [stornoNotice, setStornoNotice] = useState<string | null>(null);
  const activeStorno = (issued?.stornos ?? []).find((s) => s.status !== 'CANCELLED') ?? null;
  const canStorno = Boolean(
    issued && !issued.stornoOfId && !activeStorno && ['SENT', 'ACCEPTED'].includes(issued.status),
  );

  async function emitStorno() {
    if (!issued) return;
    const yes = await confirm({
      title: 'Emiți storno pentru această factură?',
      message:
        `Se pregătește o factură storno pentru ${inv.invoiceNumber} — aceleași linii, în minus. ` +
        'Nu pleacă nimic la ANAF acum: o verifici în fila care se deschide și o emiți de acolo.',
      confirmLabel: 'Emite storno',
    });
    if (!yes) return;
    setStornoError(null);
    setStornoNotice(null);
    try {
      const result = await storno.mutateAsync(issued.id);
      // Opened before any further await, so the browser still counts it as a
      // click and does not swallow it as a pop-up.
      window.open(`/financials/issued/${result.invoice.id}`, '_blank', 'noopener');
      setStornoNotice(
        result.validation === null
          ? 'Ciorna de storno e gata, dar validatorul ANAF nu a răspuns. Valideaz-o din fila deschisă înainte să o emiți.'
          : result.validation.ok
            ? 'Ciorna de storno e gata și a trecut validarea ANAF. Verific-o în fila deschisă și emite-o de acolo.'
            : `Ciorna de storno e gata, dar ANAF a găsit probleme: ${result.validation.messages.join(' | ')}`,
      );
    } catch (err) {
      setStornoError(
        err instanceof ApiError ? err.message : 'Factura storno nu a putut fi pregătită.',
      );
    }
  }

  async function openIssuedPdf() {
    if (!issued) return;
    setIssuedPdfBusy(true);
    setIssuedPdfError(null);
    try {
      const { url, fileName } = await fetchIssuedInvoiceFile(issued.id, 'pdf');
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setIssuedPdfError(err instanceof ApiError ? err.message : 'PDF-ul nu a putut fi descărcat.');
    } finally {
      setIssuedPdfBusy(false);
    }
  }

  const title = (
    <>
      Factură <span className="font-medium text-gray-900">{inv.invoiceNumber}</span> ·{' '}
      {SOURCE_LABELS[inv.source] ?? inv.source}
      {spv && (
        <>
          {' '}
          · mesaj SPV <span className="font-mono">{spv.anafId}</span>
          {spv.indexIncarcare && (
            <>
              {' '}
              · index încărcare <span className="font-mono">{spv.indexIncarcare}</span>
            </>
          )}
        </>
      )}{' '}
      · emisă la {date(inv.issueDate)}
    </>
  );

  const actions = (
    <>
      {spvId ? (
        <EfacturaFileButtons
          hasInvoice={spv ? spv.docType === 'factura' || spv.docType === 'storno' : true}
          busy={files.busy}
          onOpen={(kind) => void files.open(kind)}
        />
      ) : issued ? (
        <>
          <button
            type="button"
            disabled={issuedPdfBusy}
            onClick={() => void openIssuedPdf()}
            className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            PDF
          </button>
          <Link
            to={`/financials/issued/${issued.id}`}
            className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            Deschide în editor
          </Link>
        </>
      ) : (
        inv.s3Url && (
          <a
            href={inv.s3Url}
            target="_blank"
            rel="noreferrer"
            className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
          >
            PDF
          </a>
        )
      )}
      <button
        type="button"
        onClick={onEdit}
        className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
      >
        Editează
      </button>
      {issued && canStorno && (
        <button
          type="button"
          disabled={storno.isPending}
          onClick={() => void emitStorno()}
          title="Pregătește factura storno (aceleași linii, în minus) și deschide-o într-o filă nouă"
          className="rounded border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        >
          {storno.isPending ? 'Se pregătește…' : 'Emite storno'}
        </button>
      )}
      {issued && activeStorno && (
        <Link
          to={`/financials/issued/${activeStorno.id}`}
          className="rounded border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-800 hover:bg-amber-100"
        >
          Storno: {activeStorno.fullNumber ?? 'ciornă'}
        </Link>
      )}
      {issued?.stornoOfId && (
        <Link
          to={`/financials/issued/${issued.stornoOfId}`}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
        >
          Factura stornată
        </Link>
      )}
    </>
  );

  return (
    <DetailFrame
      colSpan={COLUMNS}
      title={title}
      actions={actions}
      error={files.fileError ?? issuedPdfError ?? stornoError}
      notice={stornoNotice ?? spv?.syncError ?? null}
      onClose={onClose}
    >
      {issued && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-gray-700">
          <span>Stare ANAF:</span>
          <IssuedInvoiceStatusBadge status={issued.status} />
          {issued.status === 'REJECTED' && issued.anafMessages[0] && (
            <span className="text-xs text-red-600" title={issued.anafMessages.join('\n')}>
              {issued.anafMessages[0]}
            </span>
          )}
        </div>
      )}
      <div className="mb-3">
        <ProjectPicker inv={inv} />
      </div>
      {spvId ? (
        <>
          {detail.isLoading && <p className="text-sm text-gray-500">Se încarcă…</p>}
          {spv?.details ? (
            <InvoiceDetails doc={spv.details} />
          ) : (
            !detail.isLoading && (
              <p className="text-sm text-gray-500">
                Documentul nu a putut fi interpretat. Descarcă XML-ul sau arhiva.
              </p>
            )
          )}
        </>
      ) : (
        <LocalInvoiceDetails inv={inv} ownName={ownName} />
      )}
    </DetailFrame>
  );
}

function InvoiceRow({
  inv,
  isOpen,
  companyLabel,
  ownName,
  canSettle,
  settling,
  onToggle,
  onSettle,
  onEdit,
}: {
  inv: Invoice;
  isOpen: boolean;
  companyLabel?: string;
  ownName: string;
  canSettle: boolean;
  settling: boolean;
  onToggle: () => void;
  onSettle: (settled: boolean) => void;
  onEdit: () => void;
}) {
  const rowClass = ['cursor-pointer hover:bg-gray-200', isOpen ? 'bg-brand-100' : '']
    .filter(Boolean)
    .join(' ');
  const cancelled = inv.status === 'CANCELLED';
  return (
    <>
      <tr onClick={onToggle} className={rowClass}>
        <td className="px-3 py-2">
          <KindBadge kind={kindOf(inv)} storno={inv.amount < 0} />
          {inv.source === 'firmio' && <FirmioBadge />}
          {cancelled && (
            <span className="ml-1 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500 line-through">
              anulată
            </span>
          )}
        </td>
        <td className="px-3 py-2 font-medium text-gray-900">
          {inv.invoiceNumber}
          <span className="mt-0.5 flex flex-wrap items-center gap-1 text-xs font-normal text-gray-500">
            {inv.issuedInvoice ? (
              <IssuedInvoiceStatusBadge status={inv.issuedInvoice.status} />
            ) : (
              (SOURCE_LABELS[inv.source] ?? inv.source)
            )}
            {companyLabel && <span>· {companyLabel}</span>}
          </span>
        </td>
        <td className="px-3 py-2 text-gray-700">{date(inv.issueDate)}</td>
        <td className="px-3 py-2 text-gray-700">
          {(inv.direction === 'INCOME' ? inv.client : inv.vendor) ?? '—'}
          {inv.project && <span className="block text-xs text-gray-500">{inv.project.name}</span>}
        </td>
        <td className="px-3 py-2 text-right text-gray-700">
          {money(inv.amount - inv.taxAmount, inv.currency)}
        </td>
        <td className="px-3 py-2 text-right text-gray-700">{money(inv.taxAmount, inv.currency)}</td>
        <td className="px-3 py-2 text-right font-medium text-gray-900">
          {money(inv.amount, inv.currency)}
        </td>
        <td className="px-3 py-2 text-gray-700">
          {date(inv.dueDate)}
          {inv.overdue && (
            <span className="ml-1 text-xs font-semibold text-red-600">întârziată</span>
          )}
        </td>
        <td className="px-3 py-2">
          <SettledSwitch
            label={cancelled ? null : settledLabel(inv)}
            settledAt={inv.status === 'PAID' ? (inv.paidAt ?? inv.updatedAt) : null}
            canSettle={canSettle}
            settling={settling}
            onSettle={onSettle}
          />
        </td>
      </tr>
      {isOpen && (
        <InvoiceDetailRow inv={inv} ownName={ownName} onEdit={onEdit} onClose={onToggle} />
      )}
    </>
  );
}

export function InvoicesPage() {
  const { t } = useI18n();
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const { can } = usePermissions();
  const canSettle = can('financials', 'canEditInvoices');
  const canIssue = can('financials', 'canIssueInvoices');
  const canManageSeries = can('financials', 'canApproveInvoices');
  const [showSeries, setShowSeries] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [filters, setFilters] = useState<InvoiceFilters>(DEFAULT_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useInvoices(filters);
  const { data: companies } = useCompanies();
  // Background SPV sync: refetches this list when the scheduler finishes a run.
  const syncState = useInvoiceSyncWatcher().data;
  const qc = useQueryClient();
  const setSpvSettled = useSetEfacturaSettled();
  // Rows that did not come through SPV are markdown-first: the status change
  // goes through the KB invoice PATCH, like the edit modal does.
  const setKbSettled = useMutation({
    mutationFn: (input: { invoiceId: string; settled: boolean }) =>
      updateKbInvoice(input.invoiceId, { status: input.settled ? 'PAID' : 'SENT' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err) =>
      setMarkError(err instanceof ApiError ? err.message : 'Statusul nu a putut fi schimbat.'),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const totals = data?.totals ?? [];
  const companyNames = useMemo(
    () => new Map((companies ?? []).map((c) => [c.id, c.displayName || c.legalName])),
    [companies],
  );
  const ownNameFor = (inv: Invoice): string =>
    inv.company?.name ??
    (companies ?? []).find((c) => c.isDefault)?.displayName ??
    (companies ?? []).find((c) => c.isDefault)?.legalName ??
    'Firma mea';

  function patch(p: Partial<InvoiceFilters>) {
    setFilters((prev) => ({ ...prev, ...p, page: 1 }));
    setOpenId(null);
  }

  function handleSettle(inv: Invoice, settled: boolean) {
    setMarkError(null);
    if (inv.efacturaMessageId) {
      setSpvSettled.mutate(
        { id: inv.efacturaMessageId, settled },
        {
          onError: (err) =>
            setMarkError(err instanceof ApiError ? err.message : 'Marcajul nu a putut fi salvat.'),
        },
      );
    } else {
      setKbSettled.mutate({ invoiceId: inv.id, settled });
    }
  }

  function settlingFor(inv: Invoice): boolean {
    if (inv.efacturaMessageId) {
      return setSpvSettled.isPending && setSpvSettled.variables?.id === inv.efacturaMessageId;
    }
    return setKbSettled.isPending && setKbSettled.variables?.invoiceId === inv.id;
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invoices.title')}</h1>
          <p className="text-sm text-gray-500">
            Toate facturile firmei: aduse din SPV (rămân aici după ce ANAF le șterge), emise din
            Firmio, adăugate manual sau din email.
          </p>
          {syncState?.autoSync.enabled && (
            <p className="mt-1 text-xs text-gray-400">
              {syncState.lastRunAt
                ? `Sincronizat automat din SPV la ${dateTime(syncState.lastRunAt)}`
                : 'Sincronizarea automată din SPV pornește în curând'}
              {` · la fiecare ${syncState.autoSync.intervalMinutes} min, pagina se actualizează singură`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <EditWithAIButton
            label={t('invoices.newWithAI')}
            prompt="Vreau să adaug o factură nouă. Poți și să tragi un PDF/imagine în chat și o voi extrage. Te rog cere-mi detaliile, apoi salveaz-o în Knowledge Base."
          />
          {canSettle && (
            <button
              type="button"
              onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FileSpreadsheet className="h-4 w-4" /> Importă din Excel
            </button>
          )}
          {canManageSeries && (
            <button
              type="button"
              onClick={() => setShowSeries(true)}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Hash className="h-4 w-4" /> Serii de facturare
            </button>
          )}
          {canIssue && (
            <Link
              to="/financials/issued/new"
              className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Emite factură
            </Link>
          )}
        </div>
      </div>

      <IssuedDraftsStrip />

      {showImport && (
        <InvoiceImportDialog
          onClose={() => setShowImport(false)}
          onImportComplete={() => void qc.invalidateQueries({ queryKey: ['invoices'] })}
        />
      )}

      {showSeries && (
        <InvoiceSeriesModal companyId={activeCompanyId} onClose={() => setShowSeries(false)} />
      )}

      {markError && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {markError}
        </div>
      )}

      <div className="mt-4">
        <TotalsRow totals={totals} />
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map((tab) => {
          const active = (filters.type ?? undefined) === tab.type;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => patch({ type: tab.type })}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                active
                  ? 'border-blue-600 font-medium text-blue-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <label className="ui-field-label">Caută</label>
          <input
            type="search"
            value={filters.search ?? ''}
            onChange={(e) => patch({ search: e.target.value || undefined })}
            placeholder="Partener, număr…"
            className={`${FIELD_CLASS} w-full`}
          />
        </div>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          De la
          <DateField
            value={filters.issueDateFrom ?? ''}
            onChange={(v) => patch({ issueDateFrom: v || undefined })}
            className="w-36"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          Până la
          <DateField
            value={filters.issueDateTo ?? ''}
            onChange={(v) => patch({ issueDateTo: v || undefined })}
            className="w-36"
          />
        </label>
        <div className="w-40">
          <Select
            label="Status plată"
            size="sm"
            value={filters.settled ?? ''}
            onChange={(v) =>
              patch({ settled: v === 'true' || v === 'false' ? v : undefined, status: undefined })
            }
            options={[
              { value: '', label: 'Toate' },
              { value: 'false', label: 'Deschise' },
              { value: 'true', label: 'Achitate / încasate' },
            ]}
            className="w-full"
          />
        </div>
        <div className="w-32">
          <Select
            label="Pe pagină"
            size="sm"
            value={String(filters.limit ?? 50)}
            onChange={(v) => patch({ limit: Number(v) || 50 })}
            options={[
              { value: '25', label: '25' },
              { value: '50', label: '50' },
              { value: '100', label: '100' },
            ]}
            className="w-full"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setFilters(DEFAULT_FILTERS);
            setOpenId(null);
          }}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
        >
          Șterge
        </button>
      </div>

      <div className="mt-6">
        {isLoading && <SkeletonTable rows={8} columns={COLUMNS} />}
        {isError && (
          <p className="text-red-600">
            {t('invoices.error')}
            {error instanceof Error ? `: ${error.message}` : ''}.
          </p>
        )}
        {!isLoading && !isError && (
          <FadeIn>
            {rows.length === 0 ? (
              <p className="py-8 text-center text-gray-500">{t('invoices.empty')}</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Tip</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Număr</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        Data facturii
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Partener</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Fără TVA</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">TVA</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Scadență</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">
                        {filters.type === 'INCOME'
                          ? 'Încasată'
                          : filters.type === 'OUTCOME'
                            ? 'Achitată'
                            : 'Plată'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.map((inv) => {
                      const isOpen = openId === inv.id;
                      return (
                        <InvoiceRow
                          key={inv.id}
                          inv={inv}
                          isOpen={isOpen}
                          companyLabel={
                            !activeCompanyId && inv.companyId
                              ? companyNames.get(inv.companyId)
                              : undefined
                          }
                          ownName={ownNameFor(inv)}
                          canSettle={canSettle}
                          settling={settlingFor(inv)}
                          onToggle={() => setOpenId(isOpen ? null : inv.id)}
                          onSettle={(settled) => handleSettle(inv, settled)}
                          onEdit={() => setEditing(inv)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  Pagina {meta.page} din {meta.totalPages} ({meta.total} în total)
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={meta.page <= 1}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    Următorul
                  </button>
                </div>
              </div>
            )}
          </FadeIn>
        )}
      </div>

      {editing && <InvoiceDetailModal invoice={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
